// `configureTemplatesData` — the injected-config seam (S2, ARCFE-1014;
// the `configureCodingData` / `configureWorkspaceData` pattern).
//
// TWO transports, deliberately. The scribe client family is NOT here — the
// 34 `@domain templates` endpoints read it from the `@heidi/api/client`
// registry (E5). What crosses this seam is the COMMUNITY client, which is a
// genuinely different thing: `publicCommunityApi` talks to the public,
// UNAUTHENTICATED template gallery, so it carries none of the scribe
// interceptors and must not be resolved from the scribe registry. Same
// reasoning as the EHR `inboxHttp` precedent — a domain-specific client is
// injected as its own instance.
//
// The queryClient crosses for the same reason it does in coding: module-scope
// invalidators need the app's singleton, and a package cannot own it. It now
// crosses TWICE — as the legacy instance field and as the ruled getter — and
// which one a new consumer takes is not a matter of taste; see `getQueryClient`
// below for the split and for what retires the field.
import type { QueryClient } from '@tanstack/react-query';

/**
 * Minimal structural view of the public community client — kept structural so
 * this package's public fn signatures never name axios types (declaration
 * portability in the standalone build).
 */
export interface CommunityHttpClient {
  get<T>(
    url: string,
    config?: { params?: unknown; signal?: AbortSignal }
  ): Promise<{
    data: T;
  }>;
  post<T>(url: string, body?: unknown): Promise<{ data: T }>;
  delete<T>(url: string): Promise<{ data: T }>;
}

export interface TemplatesDataConfig {
  /**
   * The app-built public community client. Unauthenticated by design: the
   * gallery is readable without a session, which is exactly why it cannot come
   * from `getScribeHttpClients()`.
   */
  communityHttp: CommunityHttpClient;
  /**
   * The app's queryClient singleton — the carved module-scope invalidators run
   * against it. LEGACY SHAPE, kept because it has live consumers; a NEW one
   * takes `getQueryClient` below.
   */
  queryClient: QueryClient;
  /**
   * Returns the app's singleton `QueryClient` — the same instance
   * `queryClient` above holds, resolved per call instead of at wiring time.
   *
   * The ONE way the app cache crosses into a package from here on. A registry
   * accessor on a platform door was the alternative and was rejected: it would
   * make the app's cache an ambient capability any package could import
   * silently, which is exactly the coupling a `configure<Domain>Data` member
   * exists to declare. The disposition is recorded under task 2.7 of
   * `openspec/changes/monorepo-ui-endgame/tasks.md`.
   *
   * WHY BOTH MEMBERS, since they resolve the same object. The field is read at
   * the moment the config is handed over, so a module evaluated before
   * `configureTemplatesData` runs gets the unset-throw; the getter resolves per
   * call, so the same module can hold it and never race client construction.
   * The creation-record fetch reads the getter at call time, so the module can
   * load before `configureTemplatesData` runs.
   * The field is not converted here: its consumers are carved and working, and
   * a conversion is its own change with its own falsifier.
   *
   * Required. A missing getter would hand that fetch no cache and no error.
   * The app wires both from the same singleton
   * (`src/utils/templates-data-wiring.ts`).
   *
   * Heals together with the field: when every consumer of the pair resolves its
   * cache per call, `queryClient` deletes.
   */
  getQueryClient: () => QueryClient;
  /**
   * "Is this error the desktop's offline network failure?" (D2.7, ARCFE-1046).
   *
   * A PLATFORM CAPABILITY, not a pure helper, which is why it crosses the seam
   * instead of moving. The real predicate
   * (`src/utils/http/desktopOfflineNetwork.ts`) reads `isTauri()` and the
   * `offlineConnectivityStateAtom` through jotai's `getDefaultStore()` — app
   * and Tauri dependencies a headless `/data` module may not hold.
   *
   * Every carved list read branches on it BEFORE deciding whether to serve a
   * cached page, swallow, or rethrow, so the branch itself is data behaviour
   * and stays in the package; only the reading of it is injected.
   */
  isOfflineNetworkError: (error: unknown) => boolean;
}

let config: TemplatesDataConfig | null = null;

export const configureTemplatesData = (next: TemplatesDataConfig): void => {
  config = next;
};

/** Fail loudly on unconfigured use — never silently degrade. */
export const getTemplatesDataConfig = (): TemplatesDataConfig => {
  if (!config) {
    throw new Error(
      '[templates/data] not configured — app bootstrap must call ' +
        'configureTemplatesData() (see src/utils/templates-data-wiring.ts); tests ' +
        'exercising real templates data modules must configure a test double first.'
    );
  }
  return config;
};
